import { useParams } from 'react-router-dom';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import './style.scss';
import Spinner from '../../Shared/Spinner';
import LegStepIndicator from '../Shared/LegStepIndicator';
import DataUrlHelper from '../../../helpers/data-url.helper';
import happyFaceIcon from '../../../../assets/icons/face-laugh-gray.svg';
import starIcon from '../../../../assets/icons/star-gray.svg';
import pokalIcon from '../../../../assets/icons/trophy-gray.svg';
import checkIcon from '../../../../assets/icons/check-gray.svg';
import stepsIcon from '../../../../assets/icons/steps-gray.svg';
import leafIcon from '../../../../assets/icons/leaf-gray.svg';
import PageIntroduction from '../Shared/PageIntroduction';
import ParticipationResultApiModel from '../models/participation-result.api-model';
import { SystemClock } from '../services/system-clock.service';

import type { IParticipationResult } from '../../../models/pa-egna-ben/participation-result';
import NotFoundRedirect from '../Shared/NotFoundRedirect';

function processReceivedData(data: ParticipationResultApiModel): IParticipationResult {
  return ParticipationResultApiModel.mapToBusinessModel(data);
}

function ParticipationOverview() {
  const { participationId } = useParams();

  // === FETCH DATA ===
  const [getParticipationResult] = useFetch<ParticipationResultApiModel>(`/api/paegnaben/participation-overview/participations/${participationId}/result`, !participationId);

  // === VARIABLES ===
  const isLoadingData = getParticipationResult.status === Status.loading;
  const numberOfCompletedExercises = getParticipationResult.data?.legResults.reduce((sum, legResult) => sum + legResult.completedExercises.length, 0);
  const participationResult = getParticipationResult.data ? processReceivedData(getParticipationResult.data) : undefined;

  function onlyShowNoOfStudents(): boolean {
    const firstLeg = participationResult?.legs.find((cl) => cl.number === 1);
    return firstLeg ? SystemClock.now() < firstLeg.endTime : false;
  }

  function numberOfStudentsElement() {
    return (
      <div className="d-flex align-items-center">
        <div className="icon-wrapper text-center">
          <img src={DataUrlHelper.encode(happyFaceIcon)} alt="Glad smiley-ikon" />
        </div>
        <span className="m-2">{participationResult?.numberOfStudents} elever</span>
      </div>
    );
  }

  // === RENDER ===
  if (getParticipationResult.status === Status.failed && getParticipationResult.httpResponseStatusCode === 404) {
    return <NotFoundRedirect />;
  }

  return (
    <div>
      {isLoadingData && <Spinner />}
      {getParticipationResult.status === Status.success && getParticipationResult.data && (
        <div>
          {participationResult && <PageIntroduction heading={participationResult.participationName} />}

          {/* NV-6591: If we have not completed the first leg, we will only show number of students */}
          {onlyShowNoOfStudents() ? (
            <div className="mb-4 mb-md-5">{numberOfStudentsElement()}</div>
          ) : (
            <div className="row mb-4 mb-md-5">
              <div className="col-md-4">
                {numberOfStudentsElement()}
                {participationResult?.weightedTotalScore !== -1 && participationResult?.rank !== -1 && (
                  <>
                    <div className="d-flex align-items-center">
                      <div className="icon-wrapper text-center">
                        <img src={DataUrlHelper.encode(starIcon)} alt="Stjärna-ikon" />
                      </div>
                      <span className="m-2">{participationResult?.weightedTotalScore} poäng</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="icon-wrapper text-center">
                        <img src={DataUrlHelper.encode(pokalIcon)} alt="Pokal-icon" />
                      </div>
                      <span className="m-2">
                        Plats {participationResult?.rank} av {participationResult?.noOfMunicipalityParticipations} i {participationResult?.municipalityName}
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-center">
                  <div className="icon-wrapper text-center">
                    <img src={DataUrlHelper.encode(checkIcon)} alt="Checkbock-ikon" />
                  </div>
                  <span className="m-2">{numberOfCompletedExercises} övningar</span>
                </div>
                {participationResult?.completedKilometers !== -1 && participationResult?.carbonDioxide !== -1 && (
                  <>
                    <div className="d-flex align-items-center">
                      <div className="icon-wrapper text-center">
                        <img src={DataUrlHelper.encode(stepsIcon)} alt="Sko-ikon" />
                      </div>
                      <span className="m-2">{participationResult?.completedKilometers} km</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="icon-wrapper text-center">
                        <img src={DataUrlHelper.encode(leafIcon)} alt="Löv-ikon" />
                      </div>
                      <span className="m-2">{participationResult && Math.round(participationResult.carbonDioxide)} kg koldioxid sparat</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="col-lg-6">
            {participationResult?.legResults && <LegStepIndicator participationId={Number(participationId)} competitionLegs={participationResult.legs} legResults={participationResult.legResults} />}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParticipationOverview;
